import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Spin } from 'antd';
import type { TKlarityPayer, TCredentialingProvider } from 'types/credentialing';
import { AllState } from 'constants/common';
import { CASHBACK_LIMIT } from '../../constants';
import SuggestPayerModal from '../SuggestPayerModal';
import PayerUnavailableTag, { isPayerUnavailable } from '../PayerUnavailableTag';
import s from './s.module.less';

interface Props {
    /** 押金已被 Ops 减免（CREDENTIALING_FEE_WAIVED）：金额划线、实付 $0 */
    feeWaived?: boolean;
    selectedPayers: string[];
    alreadyCredentialedPayers: string[];
    existingPayers: string[];
    npiNotSure: boolean;
    onSelectedPayersChange: (payers: string[]) => void;
    onBack?: () => void;
    onContinue: () => void;
    currentProvider?: TCredentialingProvider | null;
    providerPos?: number;
    providerTotal?: number;
    payerOptions: TKlarityPayer[];
    upcomingPayers: TKlarityPayer[];
    payerOptionsLoading: boolean;
    providerStates: string[];
}

const DEPOSIT_DIVISOR = 100;

const getStateName = (code: string) => AllState.find((i) => i.dictKey === code)?.content || code;

const PayerSelectStep = ({
    feeWaived,
    selectedPayers,
    alreadyCredentialedPayers,
    existingPayers,
    npiNotSure,
    onSelectedPayersChange,
    onBack,
    onContinue,
    currentProvider,
    providerPos,
    providerTotal,
    payerOptions,
    upcomingPayers,
    payerOptionsLoading,
    providerStates,
}: Props) => {
    const activePayers = useMemo(
        () => payerOptions.filter((p) => providerStates.includes(p.state)),
        [payerOptions, providerStates],
    );

    const states = useMemo(() => {
        const set = new Set(activePayers.map((p) => p.state));
        return providerStates.filter((st) => set.has(st)).sort();
    }, [activePayers, providerStates]);

    const isMulti = states.length > 1;
    const [activeState, setActiveState] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestOpen, setSuggestOpen] = useState(false);

    const effectiveState = useMemo(() => {
        if (!isMulti) return states[0] || null;
        return activeState && states.includes(activeState) ? activeState : states[0] || null;
    }, [isMulti, states, activeState]);

    const filterBySearch = <T extends { name: string }>(list: T[]) =>
        searchTerm ? list.filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase())) : list;

    const getPayersForState = (state: string) => filterBySearch(activePayers.filter((p) => p.state === state));

    const filteredActive = filterBySearch(activePayers);
    const filteredUpcoming = filterBySearch(upcomingPayers.filter((p) => providerStates.includes(p.state)));

    const selKeyFor = (payer: TKlarityPayer) => String(payer.id);

    const excludedPayerIds = useMemo(() => {
        const excluded = new Set<string>();
        for (const sel of [...selectedPayers, ...alreadyCredentialedPayers, ...existingPayers]) {
            const payer = activePayers.find((p) => selKeyFor(p) === sel);
            if (payer?.exclusivePayerIds) {
                payer.exclusivePayerIds.split(',').forEach((id) => excluded.add(id.trim()));
            }
        }
        return excluded;
    }, [selectedPayers, alreadyCredentialedPayers, existingPayers, activePayers]);

    // 已选的 payer 变成不可选（如 104 答了 No、改了执照后重新拉列表）时从选择里去掉，不收押金
    useEffect(() => {
        const blocked = selectedPayers.filter((k) => isPayerUnavailable(activePayers.find((p) => selKeyFor(p) === k)));
        if (blocked.length) onSelectedPayersChange(selectedPayers.filter((k) => !blocked.includes(k)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePayers, selectedPayers]);

    const togglePayer = (selKey: string) => {
        if (selectedPayers.includes(selKey)) {
            onSelectedPayersChange(selectedPayers.filter((k) => k !== selKey));
        } else {
            onSelectedPayersChange([...selectedPayers, selKey]);
        }
    };

    const nonAcSelected = selectedPayers.filter((k) => !alreadyCredentialedPayers.includes(k) && !existingPayers.includes(k));
    const panelCount = nonAcSelected.length;

    const depositPerPanel = useMemo(() => {
        const first = activePayers[0];
        return first ? first.depositCents / DEPOSIT_DIVISOR : 50;
    }, [activePayers]);

    const totalDeposit = panelCount * depositPerPanel;
    const cbCount = Math.min(panelCount, CASHBACK_LIMIT);
    const cbAmount = cbCount * depositPerPanel;
    // 押金已减免时没有 checkout
    const lastStepLabel = feeWaived ? 'Review and confirm' : 'Review and checkout';

    const renderPayerCard = (p: TKlarityPayer) => {
        const sel = selKeyFor(p);
        const isExisting = existingPayers.includes(sel);
        const isAlreadyCredentialed = alreadyCredentialedPayers.includes(sel);
        const isExcluded = excludedPayerIds.has(sel) && !selectedPayers.includes(sel);
        const isUnavailable = isPayerUnavailable(p);
        const isDisabled = isExisting || isAlreadyCredentialed || isExcluded || isUnavailable;
        const isSelected = selectedPayers.includes(sel);
        const showSelected = isSelected || isExisting;
        const deposit = p.depositCents / DEPOSIT_DIVISOR;

        return (
            <div
                key={sel}
                className={`${s.payerCard} ${showSelected ? s.payerCardSelected : ''} ${isDisabled ? s.payerCardDisabled : ''}`}
                onClick={() => !isDisabled && togglePayer(sel)}
            >
                <div className={`${s.payerCheck} ${showSelected ? s.payerCheckSelected : ''}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </div>
                <div className={s.payerInfo}>
                    <div className={s.payerName}>
                        {p.name}
                        {/* 执照不符时只留黄色 tag，不再叠 High demand */}
                        {p.highDemand === 1 && !isUnavailable && <span className={s.demandTag}>High demand</span>}
                        {isAlreadyCredentialed && <span className={s.demandTag}>Already credentialed</span>}
                        {isExcluded && p.payerType && <span className={s.demandTag}>Only one {p.payerType} panel is allowed</span>}
                        {isUnavailable && <PayerUnavailableTag reason={p.unavailableReason} />}
                    </div>
                </div>
                {isSelected && !isAlreadyCredentialed && !feeWaived && (
                    <span className={s.payerDeposit}>${deposit} deposit</span>
                )}
            </div>
        );
    };

    if (payerOptionsLoading) {
        return (
            <div className={s.modalBody} style={{ textAlign: 'center', padding: '60px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <>
            <div className={s.modalBody}>
                {currentProvider && providerPos != null && providerTotal != null && (
                    <div className={s.providerBar}>
                        <span className={s.providerBarText}>
                            Adding payers for <strong>{currentProvider.firstName} {currentProvider.lastName}</strong> ({providerPos + 1} of {providerTotal})
                        </span>
                        {onBack && (
                            <span className={s.providerBarChange} onClick={onBack}>Change</span>
                        )}
                    </div>
                )}
                {panelCount > 0 && (
                    <div className={s.summaryBanner}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                            <circle cx="10" cy="10" r="10" fill="#16A34A" />
                            <path d="M6 10.5l2.5 2.5L14 7.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>
                            <strong>{panelCount} panel{panelCount > 1 ? 's' : ''} selected.</strong>{' '}
                            {feeWaived ? 'No deposit needed. Your credentialing fee is waived by Klarity.' : `$${totalDeposit} in deposits.`}
                            {!feeWaived && cbCount > 0 && (
                                <> Your first {cbCount} deposit{cbCount > 1 ? 's' : ''} (${cbAmount}) come back as cashback after your first insurance claim is submitted to Klarity.</>
                            )}
                        </span>
                    </div>
                )}

                {npiNotSure && (
                    <div className={s.warningBanner}>
                        You mentioned you may already be credentialed with some payers. Please avoid selecting any panels you already have, since we can&apos;t re-do those.
                    </div>
                )}

                <Input
                    placeholder="Search payers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                    size="large"
                    style={{ marginBottom: 12 }}
                />

                {!filteredActive.length && !filteredUpcoming.length && searchTerm && (
                    <div className={s.noResults}>
                        <p>We couldn&apos;t find that in your list.</p>
                        <p>Suggest a payer and we&apos;ll consider adding it.</p>
                        <span className={s.suggestBtn} onClick={() => setSuggestOpen(true)}>Suggest a payer</span>
                    </div>
                )}

                <SuggestPayerModal
                    open={suggestOpen}
                    states={effectiveState ? [effectiveState] : []}
                    onClose={() => setSuggestOpen(false)}
                />

                {isMulti ? (
                    <>
                        <div className={s.stateChips}>
                            {states.map((st) => {
                                const cnt = getPayersForState(st).length;
                                return (
                                    <span
                                        key={st}
                                        className={`${s.stateChip} ${st === effectiveState ? s.stateChipActive : ''}`}
                                        onClick={() => setActiveState(st)}
                                    >
                                        {getStateName(st)} <span className={s.stateChipCount}>{cnt}</span>
                                    </span>
                                );
                            })}
                        </div>
                        {effectiveState && (
                            <>
                                <div className={s.sectionLabel}>Available now in {getStateName(effectiveState)}</div>
                                <div className={s.payerList}>
                                    {getPayersForState(effectiveState).map((p) => renderPayerCard(p))}
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {filteredActive.length > 0 && (
                            <>
                                <div className={s.sectionLabel}>Available now in {getStateName(states[0])}</div>
                                <div className={s.payerList}>
                                    {filteredActive.filter((p) => p.state === states[0]).map((p) => renderPayerCard(p))}
                                </div>
                            </>
                        )}
                    </>
                )}

                {filteredUpcoming.length > 0 && (
                    <div className={s.plannedSection}>
                        <p className={s.plannedTitle}>Upcoming</p>
                        <p className={s.plannedDesc}>
                            Additional payers are added regularly. You will be notified when new options become available in your state.
                        </p>
                        <div className={s.plannedGrid}>
                            {filteredUpcoming.map((p) => (
                                <div key={p.id} className={s.plannedTile}>
                                    <span>{p.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className={s.modalFooter}>
                <Button
                    type="primary"
                    block
                    size="large"
                    className={s.primaryBtn}
                    disabled={panelCount === 0}
                    onClick={onContinue}
                >
                    {providerPos != null && providerTotal != null && providerPos < providerTotal - 1
                        ? 'Continue'
                        : lastStepLabel}
                </Button>
                {onBack && (
                    <Button block size="large" className={s.backBtn} onClick={onBack}>
                        Back
                    </Button>
                )}
            </div>
        </>
    );
};

export default PayerSelectStep;
